/**
 * The push-time design lint for hub pages (wayai-pro/wayai#5333 decision 14). Design cannot be
 * enforced mechanically, so it only warns: `wayai push` and `wayai diff` print its warnings with the
 * platform's own, and a warned page is pushed like any other. The design guide it points at is
 * `skills/wayai/references/pages.md` (Designing a Page); the names it steers toward are
 * `@wayai/core/page-design`'s.
 *
 * Every rule is a text heuristic over the one HTML file, chosen to stay quiet on a page written the
 * way the guide asks. Each rule's limits, which pages.md states for builders too:
 *
 * - `hard-coded-color`: a hex color or a color function (`rgb()`, `hsl()`, `hwb()`, `lab()`, `lch()`,
 *   `oklab()`, `oklch()`) in a color property (`color`, `background`, `border*`, `outline*`, `fill`,
 *   `stroke`, …) of a `<style>` element or a `style` attribute. Not read: a `var()` fallback, a
 *   `color-mix()` from a token, a custom property's own value (a palette the page names, such as a
 *   chart's series), shadows, named colors (`white`), and colors set from script or SVG attributes.
 * - `no-theme`: the HTML never names `data-theme` (or the MCP Apps SDK's `applyDocumentTheme`). A
 *   page that names it anywhere passes, whether or not it sets it.
 * - `fixed-width`: a `width` or `inline-size` of a single pixel length over 430px, outside every
 *   `@media` and `@container` rule (a nested one included). `min-width` is not read: a table given one inside a scrolling
 *   wrapper is the pattern the guide recommends. Neither are `width` attributes.
 * - `table-overflow`: a `<table>` (in markup, or `createElement('table')`) with nothing that lets it
 *   fit a phone anywhere in the page: no `wayai-table-wrap`, no `overflow`/`overflow-x` of `auto` or
 *   `scroll`, and no table selector inside an `@media` or `@container` rule. One handled table passes
 *   every table of the page.
 * - `remote-load`: what the page CSP refuses (`default-src 'none'`; scripts and styles inline only;
 *   images as `data:` or `blob:`, fonts as `data:`, no media or frames): a `<script src>`, a
 *   stylesheet `<link>`, an image loading anything but `data:`/`blob:`, any frame, video or audio
 *   source (a `<source src>` included), a CSS `url()` or `@import` other than `data:`/`blob:`, and `fetch`,
 *   `WebSocket` or `EventSource` called with a literal URL. Markup inside script strings is not read.
 * - `list-empty-state` / `list-error-state`: only for a page that declares tools and builds a list
 *   (`<ul>`, `<ol>`, `<table>`, a `wayai-list`, or one created from script). Empty passes on
 *   `wayai-empty` or a check for zero rows (`.length === 0`, `!rows.length`); error on `isError` or
 *   `wayai-alert`, anywhere in the HTML. An inlined library that holds those words passes them too.
 */

import type { HubAsCodePage } from '@wayai/core/contracts/ci';
import { truncate } from './eval-format.js';
import { sanitizeTerminalText } from './terminal-output.js';

export type PageDesignRule =
  | 'hard-coded-color'
  | 'no-theme'
  | 'fixed-width'
  | 'table-overflow'
  | 'remote-load'
  | 'list-empty-state'
  | 'list-error-state';

export interface PageDesignFinding {
  rule: PageDesignRule;
  message: string;
}


/** The widest a fixed width may be outside a media or container query: a large phone's, in CSS px. */
export const PHONE_WIDTH_MAX_PX = 430;

// ── Splitting the HTML ──────────────────────────────────────────────────────────────────────────

const SCRIPT_ELEMENT = /<script\b([^>]*)>([\s\S]*?)<\/script\s*>/gi;
const STYLE_ELEMENT = /<style\b[^>]*>([\s\S]*?)<\/style\s*>/gi;
const TAG = /<([a-z][a-z0-9-]*)\b([^>]*)>/gi;
const ATTRIBUTE = /([a-z][a-z0-9:-]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/gi;

interface PageParts {
  /** The HTML with every script's and style's contents, and every comment, taken out. */
  markup: string;
  /** Every `<style>` element's contents and every `style` attribute (as a rule body), in order. */
  css: Array<{ text: string; attribute: boolean }>;
  /** Every inline script's contents. */
  scripts: string;
}

function attributesOf(tag: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const m of tag.matchAll(ATTRIBUTE)) out.set(m[1].toLowerCase(), (m[2] ?? m[3] ?? m[4] ?? '').trim());
  return out;
}

function splitPage(html: string): PageParts {
  const scripts: string[] = [];
  const withoutScripts = html.replace(SCRIPT_ELEMENT, (_m, attrs: string, body: string) => {
    scripts.push(body);
    return `<script${attrs}></script>`;
  });
  const withoutComments = withoutScripts.replace(/<!--[\s\S]*?-->/g, '');
  const css: PageParts['css'] = [];
  const markup = withoutComments.replace(STYLE_ELEMENT, (_m, body: string) => {
    css.push({ text: body, attribute: false });
    return '<style></style>';
  });
  for (const tag of markup.matchAll(TAG)) {
    const style = attributesOf(tag[2]).get('style');
    if (style) css.push({ text: style, attribute: true });
  }
  return { markup, css, scripts: scripts.join('\n') };
}

// ── Reading the CSS ─────────────────────────────────────────────────────────────────────────────

interface Declaration {
  prop: string;
  value: string;
  /** The preludes of the blocks it sits in, outermost first, its own rule's selector last. */
  context: string[];
}

interface CssRead {
  declarations: Declaration[];
  /** The URLs it loads: each `url()` and `@import`. */
  loads: string[];
}

/** The text of `name(…)` calls in `value` removed, parentheses balanced; only those `keep` refuses. */
function removeCalls(value: string, name: string, keep: (inner: string) => boolean = () => false): string {
  const lower = value.toLowerCase();
  const open = `${name}(`;
  let out = '';
  let from = 0;
  for (let at = lower.indexOf(open); at !== -1; at = lower.indexOf(open, from)) {
    let depth = 0;
    let end = at + name.length;
    for (; end < value.length; end++) {
      if (value[end] === '(') depth++;
      else if (value[end] === ')' && --depth === 0) break;
    }
    const inner = value.slice(at + open.length, end);
    out += value.slice(from, at) + (keep(inner) ? value.slice(at, end + 1) : '');
    from = end + 1;
  }
  return out + value.slice(from);
}

const URL_CALL = /url\(\s*(?:"([^"]*)"|'([^']*)'|([^)]*?))\s*\)/gi;
const IMPORT_RULE = /@import\s+(?:url\(\s*)?["']?([^"')\s;]*)[^;]*;?/gi;

function readCss(source: string, attribute: boolean): CssRead {
  const loads: string[] = [];
  let css = source.replace(/\/\*[\s\S]*?\*\//g, '');
  css = css.replace(IMPORT_RULE, (_m, url: string) => {
    loads.push(url || '@import');
    return '';
  });
  css = css.replace(URL_CALL, (_m, a?: string, b?: string, c?: string) => {
    loads.push((a ?? b ?? c ?? '').trim());
    return 'url()';
  });
  // Strings hold no declaration of their own, and may hold `;`, `{` or `}`.
  css = css.replace(/"[^"]*"|'[^']*'/g, '""');

  const declarations: Declaration[] = [];
  const declare = (body: string, context: string[]) => {
    for (const piece of body.split(';')) {
      const colon = piece.indexOf(':');
      if (colon === -1) continue;
      const prop = piece.slice(0, colon).trim().toLowerCase();
      const value = piece.slice(colon + 1).replace(/\s+/g, ' ').trim();
      if (/^-?-?[a-z][a-z0-9-]*$/.test(prop) && value) declarations.push({ prop, value, context });
    }
  };

  if (attribute) {
    declare(css, []);
    return { declarations, loads };
  }
  const stack: Array<{ prelude: string; body: string }> = [];
  let text = '';
  for (const ch of css) {
    if (ch === '{') {
      stack.push({ prelude: text.trim(), body: '' });
      text = '';
    } else if (ch === '}') {
      const block = stack.pop();
      if (block) declare(block.body + text, [...stack.map((b) => b.prelude), block.prelude]);
      text = '';
    } else if (ch === ';') {
      if (stack.length > 0) stack[stack.length - 1].body += `${text};`;
      text = '';
    } else {
      text += ch;
    }
  }
  return { declarations, loads };
}

/** Whether a declaration sits inside an `@media` or `@container` rule, a nested one included. */
const inQuery = (d: Declaration) => d.context.some((p) => /^@(?:media|container)\b/i.test(p));

// ── The rules ───────────────────────────────────────────────────────────────────────────────────

const COLOR_PROPERTY =
  /^(?:color|background|background-color|fill|stroke|caret-color|accent-color|text-decoration|text-decoration-color|outline|outline-color|column-rule|column-rule-color)$|^border(?:-(?:top|right|bottom|left|block|inline|block-start|block-end|inline-start|inline-end))?(?:-color)?$/;
const HEX_COLOR = /#(?:[0-9a-f]{8}|[0-9a-f]{6}|[0-9a-f]{3,4})(?![\w-])/i;
const COLOR_FUNCTION = /\b(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch)\(/i;

function hardCodedColor(d: Declaration): boolean {
  if (!COLOR_PROPERTY.test(d.prop)) return false;
  const tokenMixesGone = removeCalls(d.value, 'color-mix', (inner) => !/var\(/i.test(inner));
  const literal = removeCalls(tokenMixesGone, 'var');
  return HEX_COLOR.test(literal) || COLOR_FUNCTION.test(literal);
}

function fixedWidth(d: Declaration): boolean {
  if (d.prop !== 'width' && d.prop !== 'inline-size') return false;
  const px = /^(\d+(?:\.\d+)?)px$/i.exec(d.value.replace(/\s*!important$/i, ''));
  return px !== null && Number(px[1]) > PHONE_WIDTH_MAX_PX && !inQuery(d);
}

const TABLE_SELECTOR = /\b(?:table|thead|tbody|tr|th|td)\b/i;

function tableFits(html: string, declarations: Declaration[]): boolean {
  if (html.includes('wayai-table-wrap')) return true;
  return declarations.some((d) =>
    (/^overflow(?:-x|-inline)?$/.test(d.prop) && /\b(?:auto|scroll)\b/.test(d.value))
    || (inQuery(d) && d.context.some((p) => !p.startsWith('@') && TABLE_SELECTOR.test(p))));
}

const LOCAL_URL = /^(?:data:|blob:|#)/i;
/**
 * Elements whose source may be a `data:` or `blob:` URL under the page CSP (`img-src data: blob:`). A
 * `<source>`'s `srcset` is a `<picture>`'s image; its `src` is a video's or audio's (`MEDIA_ELEMENTS`).
 */
const IMAGE_ELEMENTS = new Set(['img', 'input', 'image']);
/** Elements the page CSP refuses to load anything into: it sets no `media-src` or `frame-src`, so `default-src 'none'` holds. */
const MEDIA_ELEMENTS = new Set(['video', 'audio', 'track', 'iframe', 'embed', 'frame']);
const SCRIPT_LOAD = /\b(?:fetch|WebSocket|EventSource)\s*\(\s*(['"`])((?:https?:|wss?:|\/\/)[^'"`]*)\1/g;

function markupLoads(markup: string): string[] {
  const loads: string[] = [];
  for (const tag of markup.matchAll(TAG)) {
    const name = tag[1].toLowerCase();
    const attrs = attributesOf(tag[2]);
    if (name === 'script') {
      const src = attrs.get('src');
      if (src) loads.push(src);
    } else if (name === 'link') {
      const href = attrs.get('href');
      const rel = attrs.get('rel') ?? '';
      if (href && (/\bstylesheet\b/i.test(rel) || (/icon|preload|prefetch|manifest/i.test(rel) && !LOCAL_URL.test(href)))) {
        loads.push(href);
      }
    } else if (name === 'source') {
      const src = attrs.get('src');
      const srcset = attrs.get('srcset');
      if (src) loads.push(src);
      if (srcset && !LOCAL_URL.test(srcset)) loads.push(srcset);
    } else if (IMAGE_ELEMENTS.has(name)) {
      for (const key of ['src', 'srcset', 'href']) {
        const url = attrs.get(key);
        if (url && !LOCAL_URL.test(url)) loads.push(url);
      }
    } else if (MEDIA_ELEMENTS.has(name)) {
      for (const key of ['src', 'poster']) {
        const url = attrs.get(key);
        if (url) loads.push(url);
      }
    } else if (name === 'object') {
      const data = attrs.get('data');
      if (data) loads.push(data);
    }
  }
  return loads;
}

const LIST_MARKUP = /<(?:ul|ol|table)\b|wayai-list|createElement\(\s*['"`](?:li|tr|ul|ol|table)['"`]/i;
const ZERO_ROWS_CHECK = /\.length\s*(?:===?|<=)\s*0\b|\.length\s*<\s*1\b|!\s*[\w$.[\]]+\.length\b/;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
/** A snippet of the page quoted in a warning, on one line: the page may be anyone's (a pull), so no control characters or line breaks. */
const shown = (text: string) => truncate(sanitizeTerminalText(text).replace(/\s+/g, ' '), 60);

/** What `page`'s HTML breaks of the design guide, one finding per rule, in a fixed order. */
export function lintPageDesign(page: HubAsCodePage): PageDesignFinding[] {
  const { html } = page;
  const parts = splitPage(html);
  const read = parts.css.map((c) => readCss(c.text, c.attribute));
  const declarations = read.flatMap((r) => r.declarations);
  const findings: PageDesignFinding[] = [];

  const colors = declarations.filter(hardCodedColor);
  if (colors.length > 0) {
    findings.push({
      rule: 'hard-coded-color',
      message: `${plural(colors.length, 'hard-coded color')} in its CSS, e.g. \`${shown(`${colors[0].prop}: ${colors[0].value}`)}\`: style with the tokens (var(--color-...), var(--wayai-...)) so the page follows the viewer's light or dark theme`,
    });
  }

  if (!/data-theme|applyDocumentTheme/.test(html)) {
    findings.push({
      rule: 'no-theme',
      message: "never sets data-theme on <html> from the host context's theme, so it keeps the theme it opened in when the viewer switches between light and dark",
    });
  }

  const widths = declarations.filter(fixedWidth);
  if (widths.length > 0) {
    findings.push({
      rule: 'fixed-width',
      message: `${plural(widths.length, 'fixed width')} wider than a phone (${PHONE_WIDTH_MAX_PX}px) outside a media or container query, e.g. \`${widths[0].prop}: ${widths[0].value}\`: lay the page out mobile-first and widen it inside a media or container query`,
    });
  }

  if ((/<table\b/i.test(html) || /createElement\(\s*['"`]table['"`]/.test(html)) && !tableFits(html, declarations)) {
    findings.push({
      rule: 'table-overflow',
      message: 'has a table with no sideways scroll or narrow layout: wrap it in a wayai-table-wrap element, or turn its rows into cards inside a media or container query',
    });
  }

  const loads = [
    ...markupLoads(parts.markup),
    ...read.flatMap((r) => r.loads).filter((url) => url !== '' && !LOCAL_URL.test(url)),
    ...[...parts.scripts.matchAll(SCRIPT_LOAD)].map((m) => m[2]),
  ];
  if (loads.length > 0) {
    findings.push({
      rule: 'remote-load',
      message: `${plural(loads.length, 'load')} the page's sandbox refuses, e.g. \`${shown(loads[0])}\`: a page has no network, so inline scripts and styles, and give images and fonts as data: URLs`,
    });
  }

  const tools = page.manifest.tools;
  if (Array.isArray(tools) && tools.length > 0 && LIST_MARKUP.test(html)) {
    if (!html.includes('wayai-empty') && !ZERO_ROWS_CHECK.test(html)) {
      findings.push({
        rule: 'list-empty-state',
        message: 'fills a list from its tools but shows no empty state (no wayai-empty element and no check for zero rows): say what is missing when a list comes back empty',
      });
    }
    if (!/\bisError\b/.test(html) && !html.includes('wayai-alert')) {
      findings.push({
        rule: 'list-error-state',
        message: "calls its tools but never reads a result's isError or shows a wayai-alert: show the viewer why a call failed",
      });
    }
  }
  return findings;
}

/** Push's and diff's design warnings for a hub folder's pages: `page "<slug>" (design): …`. */
export function pageDesignWarnings(pages: readonly HubAsCodePage[] | undefined): string[] {
  if (!pages) return [];
  // `readPages` names every page by its folder, a valid slug.
  return pages.flatMap((page) => lintPageDesign(page).map((f) => `page "${String(page.manifest.slug)}" (design): ${f.message}`));
}
